import { schemes, zSchema } from "../../schema-validation";
import { ETxnSpeed } from "../../transaction-speeds";
import { EDisbursementMethod } from "../../workers-shared";
import { EContractorPaymentStatus } from "../EContractorPaymentStatus";
import { EContractorPaymentType } from "../EContractorPaymentType";
import { basePaymentItemResponseSchema } from "./paymentItemSchema";

// Base schema for full contractor payment (includes all DB fields)
export const contractorPaymentSchema = zSchema.object({
  _id: schemes.mongoObjectId(),
  contractorPaymentID: schemes.mongoObjectId(),
  /**
   * @deprecated Use `totals.gross_amount` and `totals.net_amount` instead. This field is maintained for backward compatibility.
   * For payments with `has_payment_items: true`, use `totals.gross_amount` for gross amount or `totals.net_amount` for net amount.
   */
  amount: zSchema.number().nullish(),
  approval_required: zSchema.boolean(),
  approved: zSchema.boolean(),
  companyID: schemes.companyID(),
  contractorID: schemes.mongoObjectId(),
  createdAt: zSchema.string(),
  customerAccountID: schemes.uuid().nullish(),
  disbursement: zSchema.object({
    history: zSchema.array(
      zSchema.object({
        date: zSchema.string(),
        status: zSchema.string(),
      })
    ),
    method: zSchema.nativeEnum(EDisbursementMethod),
    status: zSchema
      .enum([
        "pending",
        "settled",
        "canceled",
        "returned",
        "net_pay_void",
        "refunded",
      ])
      .nullish(),
  }),
  first_name: zSchema.string(),
  middle_name: zSchema.string().nullish(),
  last_name: zSchema.string(),
  group_id: schemes.uuid().optional(),
  has_payment_items: zSchema.boolean(),
  pay_date: schemes.isoDate(),
  payment_items: zSchema.array(basePaymentItemResponseSchema).optional(),
  deductions: zSchema
    .array(
      zSchema.object({
        additional_fields: zSchema.record(zSchema.unknown()).optional(),
        companyID: schemes.companyID(),
        deduction_type: zSchema.string(),
        deduction_credit: zSchema.boolean(),
        deductionID: schemes.mongoObjectId(),
        deductionTemplateID: schemes.mongoObjectId().optional(),
        deduction_template_name: zSchema.string(),
        employee_calculated_contribution: zSchema.number(),
        employee_contribution: zSchema
          .object({
            value: zSchema.number(),
            contribution_type: zSchema.enum(["cents", "percentage", "dollars"]),
          })
          .optional(),
        employeeCheckID: schemes.mongoObjectId(),
        employer_calculated_contribution: zSchema.number().optional(),
        employer_contribution: zSchema
          .object({
            value: zSchema.number(),
            contribution_type: zSchema.enum(["cents", "percentage", "dollars"]),
            matching: zSchema.boolean().optional(),
          })
          .optional(),
      })
    )
    .optional(),
  speed: zSchema.nativeEnum(ETxnSpeed).optional(),
  status: zSchema.nativeEnum(EContractorPaymentStatus),
  type: zSchema.nativeEnum(EContractorPaymentType),
  updatedAt: zSchema.string(),
  metadata: zSchema.record(zSchema.string(), zSchema.any()),
  totals: zSchema.object({
    company_cash_requirement: zSchema.number().nullish(),
    company_debit: zSchema.number().nullish(),
    gross_amount: zSchema.number(),
    net_amount: zSchema.number(),
    deductions: zSchema.number(),
    bill_amount: zSchema.number(),
  }),
});

export type IContractorPaymentResponse = zSchema.infer<
  typeof contractorPaymentSchema
>;
